import { useState, useEffect } from 'react'
import { useNavigate, useSearchParams, Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import { supabase } from '@/lib/supabase'
import { Eye, EyeOff, Lock, UserPlus, ArrowRight } from 'lucide-react'
import { getHomeLink } from '@/config/domains'
import { FramedWordmark } from '@/components/FramedLogo'

export const AcceptInvite = () => {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const [loading, setLoading] = useState(false)
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [error, setError] = useState('')
  const [email, setEmail] = useState('')
  const [projectId, setProjectId] = useState<string | null>(null)
  const [projectType, setProjectType] = useState<string | null>(null)

  useEffect(() => {
    // Check if there's an error in the URL (expired link, etc.)
    const hashParams = new URLSearchParams(window.location.hash.substring(1))
    const errorParam = hashParams.get('error')

    if (errorParam) {
      setError('This invitation link is invalid or has expired. Please request a new invitation.')
      return
    }

    // Get project ID from URL params
    const projectIdParam = searchParams.get('projectId')
    if (projectIdParam) {
      setProjectId(projectIdParam)
      // Fetch project type to determine redirect destination
      supabase
        .from('projects')
        .select('project_type')
        .eq('id', projectIdParam)
        .single()
        .then(({ data }) => {
          if (data?.project_type) {
            setProjectType(data.project_type)
          }
        })
    }

    // Get the current session to see if invitation was valid
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session?.user) {
        setEmail(session.user.email || '')
      } else {
        setError('This invitation link is invalid or has expired. Please request a new invitation.')
      }
    })
  }, [searchParams])

  const handleSetPassword = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')

    if (!password || !confirmPassword) {
      setError('Please fill in both password fields.')
      return
    }

    if (password.length < 8) {
      setError('Password must be at least 8 characters long.')
      return
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match.')
      return
    }

    setLoading(true)

    try {
      // Update the user's password
      const { data: updateData, error: updateError } = await supabase.auth.updateUser({
        password: password,
      })

      if (updateError) throw updateError

      // Mark any 'invited' project_members rows for this user as 'active'
      if (updateData?.user?.id) {
        await supabase
          .from('project_members')
          .update({ invite_status: 'active' })
          .eq('user_id', updateData.user.id)
          .eq('invite_status', 'invited')
      }

      // Success - redirect to the appropriate page
      if (projectId) {
        // Redirect to the project - discovery view for standard, default for review-only
        const view = projectType === 'review_only' ? '' : 'discovery'
        navigate(view ? `/project/${projectId}?view=${view}` : `/project/${projectId}`)
      } else {
        // No project context, go to dashboard
        navigate('/dashboard')
      }
    } catch (error: unknown) {
      console.error('Error setting password:', error)
      setError('Something went wrong. Please try again or contact support.')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen bg-[#FAFAFA] overflow-hidden">
      {/* Subtle background grid */}
      <div
        className="fixed inset-0 pointer-events-none"
        style={{
          backgroundImage: `
            linear-gradient(to right, #E5E5E5 1px, transparent 1px),
            linear-gradient(to bottom, #E5E5E5 1px, transparent 1px)
          `,
          backgroundSize: '40px 40px',
        }}
      />

      {/* Yellow glow accents */}
      <div className="fixed top-20 right-20 w-[400px] h-[400px] bg-[#FFD500] rounded-full blur-[150px] opacity-20 pointer-events-none" />
      <div className="fixed bottom-20 left-20 w-[300px] h-[300px] bg-[#FFD500] rounded-full blur-[120px] opacity-15 pointer-events-none" />

      {/* Navigation */}
      <nav className="relative z-10">
        <div className="max-w-6xl mx-auto px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            {/* Logo */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              className="flex items-center gap-3"
            >
              <Link to={getHomeLink()}>
                <FramedWordmark className="text-2xl" />
              </Link>
            </motion.div>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <div className="relative z-10 flex items-center justify-center min-h-[calc(100vh-80px)] px-6 py-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="w-full max-w-md"
        >
          {/* Header */}
          <div className="text-center mb-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-framed-neutral-600 bg-framed-neutral-200 rounded-full mb-6"
            >
              <UserPlus className="w-4 h-4" />
              You've been invited
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="text-3xl sm:text-4xl font-semibold text-framed-neutral-800 tracking-tight mb-3"
            >
              Welcome to Framed
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="text-framed-neutral-500"
            >
              Set up your password to get started
              {email && <span className="block mt-1 text-framed-neutral-500 text-sm">{email}</span>}
            </motion.p>
          </div>

          {/* Card */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="bg-white border border-framed-neutral-300 rounded-2xl p-8 shadow-sm"
          >
            {/* Error state when invitation is invalid */}
            {error && !email ? (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="text-center"
              >
                <div className="w-16 h-16 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl">⚠️</span>
                </div>
                <p className="text-red-600 mb-6">{error}</p>
                <Link
                  to="/login"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-framed-neutral-200 hover:bg-framed-neutral-300 text-framed-neutral-800 font-medium rounded-xl transition-colors"
                >
                  Go to Login
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </motion.div>
            ) : (
              <form onSubmit={handleSetPassword} className="space-y-5">
                {/* Error message */}
                {error && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="p-4 bg-red-50 border border-red-100 rounded-xl text-sm text-red-600"
                  >
                    {error}
                  </motion.div>
                )}

                {/* Password field */}
                <div>
                  <label htmlFor="password" className="block text-sm font-medium text-framed-neutral-700 mb-2">
                    Password
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                      <Lock className="h-5 w-5 text-framed-neutral-500" />
                    </div>
                    <input
                      type={showPassword ? 'text' : 'password'}
                      id="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="block w-full pl-12 pr-12 py-3.5 bg-white border border-framed-neutral-300 hover:border-framed-neutral-400 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#FFD500]/30 focus:border-[#FFD500] text-framed-neutral-800 placeholder-framed-neutral-500 transition-all"
                      placeholder="Create a password"
                      disabled={loading}
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute inset-y-0 right-0 pr-4 flex items-center text-framed-neutral-500 hover:text-framed-neutral-600 transition-colors"
                    >
                      {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                </div>

                {/* Confirm password field */}
                <div>
                  <label htmlFor="confirmPassword" className="block text-sm font-medium text-framed-neutral-700 mb-2">
                    Confirm Password
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                      <Lock className="h-5 w-5 text-framed-neutral-500" />
                    </div>
                    <input
                      type={showPassword ? 'text' : 'password'}
                      id="confirmPassword"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      className="block w-full pl-12 pr-4 py-3.5 bg-white border border-framed-neutral-300 hover:border-framed-neutral-400 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#FFD500]/30 focus:border-[#FFD500] text-framed-neutral-800 placeholder-framed-neutral-500 transition-all"
                      placeholder="Confirm your password"
                      disabled={loading}
                      required
                    />
                  </div>
                </div>

                {/* Submit button */}
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full py-3.5 px-6 bg-[#FFD500] hover:bg-[#E6C000] disabled:bg-framed-neutral-400 text-[#0a0a0a] font-semibold rounded-xl transition-all duration-200 flex items-center justify-center gap-2 shadow-lg shadow-[#FFD500]/20 hover:shadow-[#FFD500]/30 hover:-translate-y-0.5 disabled:shadow-none disabled:translate-y-0 group"
                >
                  {loading ? (
                    <motion.div
                      animate={{ rotate: 360 }}
                      transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                      className="w-5 h-5 border-2 border-[#0a0a0a]/30 border-t-[#0a0a0a] rounded-full"
                    />
                  ) : (
                    <>
                      Get Started
                      <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
                    </>
                  )}
                </button>

                {/* Helper text */}
                <p className="text-sm text-framed-neutral-500 text-center leading-relaxed">
                  Your password must be at least 8 characters long.
                </p>
              </form>
            )}
          </motion.div>

          {/* Footer */}
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.6 }}
            className="text-center text-sm text-framed-neutral-500 mt-8"
          >
            &copy; {new Date().getFullYear()} Framed. All rights reserved.
          </motion.p>
        </motion.div>
      </div>
    </div>
  )
}
